import type { ReactNode } from 'react';
import Badge from '@/components/ui/Badge/Badge';
import Button from '@/components/ui/Button/Button';
import styles from './HeroSection.module.css';

export interface HeroAction {
  id?: string;
  label: string;
  variant?: 'primary' | 'ghost' | 'gradient';
  icon?: ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export interface HeroMeta {
  items: string[];
  rightsIndex?: number; // index of the rights-cleared item
}

interface HeroSectionProps {
  badgeText: string;
  badgeVariant?: 'green' | 'cyan';
  title: string;
  player: ReactNode;
  sidePanel: ReactNode;
  description: string;
  tags?: string[];
  meta: HeroMeta;
  actions: HeroAction[];
}

export default function HeroSection({
  badgeText,
  badgeVariant = 'green',
  title,
  player,
  sidePanel,
  description,
  tags,
  meta,
  actions,
}: HeroSectionProps) {
  return (
    <section className={styles.hero}>
      {/* Title bar */}
      <div className={styles.titleBar}>
        <Badge variant="spotlight" withPulse={badgeVariant === 'green'}>
          {badgeText}
        </Badge>
        <h1 className={styles.title}>
          {title}
        </h1>
      </div>

      {/* Main stage: player + side panel */}
      <div className={styles.stage}>
        <div className={styles.playerWrap}>{player}</div>
        {sidePanel}
      </div>

      {/* Below the stage */}
      <div className={styles.below}>
        <p className={styles.sub}>{description}</p>
        {tags && tags.length > 0 && (
          <div className={styles.tags}>
            {tags.map((tag) => (
              <span key={tag} className={styles.tag}>{tag}</span>
            ))}
          </div>
        )}
        <div className={styles.meta}>
          {meta.items.map((item, idx) => (
            <span
              key={idx}
              className={
                item === '·'
                  ? styles.metaSep
                  : meta.rightsIndex === idx
                  ? styles.metaRights
                  : undefined
              }
            >
              {item}
            </span>
          ))}
        </div>
        <div className={styles.btns}>
          {actions.map((action) => (
            <Button
              key={action.id ?? action.label}
              variant={action.variant ?? 'ghost'}
              onClick={action.onClick}
              style={action.style}
            >
              {action.icon}
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}
